import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import WhatsAppFloat from '@/components/feature/WhatsAppFloat';
import SectionHeading from '@/components/base/SectionHeading';
import { useSEO } from '@/hooks/useSEO';

export default function ContatoPage() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  useSEO({
    title: 'Contato e Orçamento | Home Office Premium – Caruaru PE',
    description: 'Entre em contato com a Home Office Premium em Caruaru PE. Solicite orçamento de móveis corporativos e escolares, agende visita ao showroom ou fale pelo WhatsApp (81) 98203-0285.',
    keywords: 'contato Home Office Premium, orçamento móveis Caruaru, showroom móveis corporativos Pernambuco, móveis corporativos Caruaru, cadeiras ergonômicas Caruaru PE',
    path: '/contato',
    ogTitle: 'Contato e Orçamento | Home Office Premium Caruaru',
    ogDescription: 'Solicite orçamento de móveis corporativos em Caruaru PE. WhatsApp (81) 98203-0285.',
  });

  useEffect(() => {
    const schemaId = 'schema-contato';
    let script = document.getElementById(schemaId) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.id = schemaId;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    const siteUrl = import.meta.env.VITE_SITE_URL ?? '';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'ContactPage',
          name: 'Contato – Home Office Premium',
          description: 'Página de contato da Home Office Premium em Caruaru PE.',
          url: `${siteUrl}/contato`,
        },
        {
          '@type': 'LocalBusiness',
          '@id': `${siteUrl}/#localbusiness`,
          name: 'Home Office Premium',
          description: 'Loja de móveis corporativos, cadeiras ergonômicas, divisórias, cabines acústicas e projetos turnkey em Caruaru PE. Atendemos todo Pernambuco.',
          url: siteUrl,
          telephone: '+55-81-98203-0285',
          email: 'ednilsonleocadio@gmail.com',
          image: '/media/marca/78ce-d393-45cb-9139-6a3902421a65_captura-de-tela-2026-04-04-231524.png',
          logo: '/media/marca/d74127ff-a8d6-40b5-acfb-37c019b82644_horizontal-branco.png',
          address: {
            '@type': 'PostalAddress',
            streetAddress: 'Av. Agamenon Magalhães, 1519',
            addressLocality: 'Caruaru',
            addressRegion: 'PE',
            postalCode: '55014-000',
            addressCountry: 'BR',
          },
          geo: { '@type': 'GeoCoordinates', latitude: -8.2836, longitude: -36.0078 },
          openingHoursSpecification: [
            { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'], opens: '08:00', closes: '18:00' },
            { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Saturday'], opens: '09:00', closes: '13:00' },
          ],
          areaServed: [
            { '@type': 'City', name: 'Caruaru' },
            { '@type': 'City', name: 'Recife' },
            { '@type': 'City', name: 'Petrolina' },
            { '@type': 'City', name: 'Garanhuns' },
            { '@type': 'City', name: 'Arcoverde' },
            { '@type': 'City', name: 'Santa Cruz do Capibaribe' },
            { '@type': 'City', name: 'Bezerros' },
            { '@type': 'City', name: 'Gravatá' },
            { '@type': 'State', name: 'Pernambuco' },
          ],
          sameAs: ['https://share.google/OGM77XTqEJtOrXDY8'],
          priceRange: '$$',
          currenciesAccepted: 'BRL',
          paymentAccepted: 'Cash, Credit Card, Bank Transfer, PIX',
        },
      ],
    });
    return () => { script?.remove(); };
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const textarea = form.querySelector('textarea');
    if (textarea && textarea.value.length > 500) { alert('A mensagem não pode ter mais de 500 caracteres.'); return; }
    setLoading(true);
    const data = new URLSearchParams();
    Array.from(new FormData(form)).forEach(([k, v]) => data.append(k, v as string));
    try {
      await fetch('https://readdy.ai/api/form/d78qf1adqn9mib2tqs30', { method: 'POST', body: data });
      setSubmitted(true);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Hero fachada */}
      <section className="relative h-[60vh] min-h-[420px] overflow-hidden bg-foreground-950">
        <img
          src="/media/marca/78ce-d393-45cb-9139-6a3902421a65_captura-de-tela-2026-04-04-231524.png"
          alt="Fachada Home Office Premium"
          loading="eager"
          fetchPriority="high"
          decoding="sync"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/70 via-foreground-950/40 to-foreground-950/85" />
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-[1200px] mx-auto px-5 md:px-8 pb-16 md:pb-20 w-full">
            <span className="text-primary-500 text-[11px] font-semibold tracking-[0.2em] uppercase">Fale conosco</span>
            <h1 className="mt-3 text-[36px] md:text-[52px] font-semibold text-white leading-[1.05] tracking-tight">
              Contato.
            </h1>
            <p className="mt-4 text-[15px] text-white/70 leading-relaxed max-w-lg font-light">
              Solicite um orçamento, tire dúvidas ou agende uma visita ao nosso showroom. Respondemos em até 24 horas.
            </p>
          </div>
        </div>
      </section>

      {/* Showroom com hora marcada */}
      <section className="bg-white">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-20 md:py-28">
          <SectionHeading
            eyebrow="Showroom"
            title="Visite com hora marcada."
            subtitle="Aqui o atendimento é personalizado. Reservamos um tempo só pra você — com café, bolo e atenção total aos detalhes do seu projeto."
            align="center"
            className="mb-14"
          />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <div className="bg-foreground-950 rounded-2xl p-8 md:p-10">
              <div className="flex items-center gap-3 mb-6">
                <span className="w-11 h-11 flex items-center justify-center rounded-full bg-white/10 text-primary-400">
                  <i className="ri-calendar-check-line text-xl"></i>
                </span>
                <div>
                  <h3 className="text-white font-medium text-[17px]">Com horário marcado</h3>
                  <p className="text-white/50 text-[13px] font-light">A experiência completa</p>
                </div>
              </div>
              <ul className="space-y-4">
                {[
                  'Atendimento exclusivo de 30 minutos a 1 hora só pra você',
                  'Qualquer dia e horário — sábado à noite, domingo de manhã, após as 20h',
                  'Ninguém vai interromper. Só você e a gente',
                  'Te recebemos com café e bolo, como cliente especial',
                  'Tempo dedicado pra observar acabamentos, tecidos, cores e ergonomia',
                ].map((text) => (
                  <li key={text} className="flex items-start gap-3">
                    <i className="ri-check-line text-primary-400 mt-0.5"></i>
                    <span className="text-white/70 text-[14px] leading-relaxed font-light">{text}</span>
                  </li>
                ))}
              </ul>
              <a
                href="https://wa.me/5581982030285?text=Oi%2C%20gostaria%20de%20agendar%20uma%20visita%20ao%20showroom%20da%20Home%20Office%20Premium!"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-primary-600 text-white font-medium text-[15px] py-3.5 rounded-full hover:bg-primary-700 transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                Agendar pelo WhatsApp
              </a>
            </div>

            <div className="bg-background-50 rounded-2xl p-8 md:p-10 border border-background-200/50">
              <div className="flex items-center gap-3 mb-6">
                <span className="w-11 h-11 flex items-center justify-center rounded-full bg-background-200/60 text-foreground-500">
                  <i className="ri-user-search-line text-xl"></i>
                </span>
                <div>
                  <h3 className="text-foreground-900 font-medium text-[17px]">Sem agendamento</h3>
                  <p className="text-foreground-400 text-[13px] font-light">Nem sempre é a melhor opção</p>
                </div>
              </div>
              <ul className="space-y-4">
                {[
                  'Podemos estar atendendo 2 ou 3 pessoas ao mesmo tempo',
                  'O atendimento fica dividido, sem o foco que seu projeto merece',
                  'Você não consegue observar os detalhes com calma',
                  'Sem agendamento, infelizmente não rola aquele café com bolo',
                ].map((text) => (
                  <li key={text} className="flex items-start gap-3">
                    <i className="ri-close-line text-foreground-300 mt-0.5"></i>
                    <span className="text-foreground-500 text-[14px] leading-relaxed font-light">{text}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-8 text-foreground-400 text-[13px] font-light leading-relaxed border-t border-background-200/60 pt-6">
                Claro, se passar aqui na frente e quiser dar uma olhada rápida, a porta está sempre aberta. Mas pra sentar e entender seu projeto com calma — <strong className="text-foreground-700 font-medium">agenda com a gente.</strong>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Horário flexível */}
      <section className="bg-background-50">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-20 md:py-24">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-[26px] md:text-[34px] font-semibold text-foreground-900 leading-[1.1] tracking-tight">
              Horário flexível de verdade.
            </h2>
            <p className="mt-4 text-[15px] text-foreground-500 leading-relaxed font-light">
              Não temos um horário fixo porque cada cliente tem uma rotina diferente. Sábado às 19h? Domingo às 10h? Dia de semana às 20h? A gente se adapta à sua agenda. É só chamar no WhatsApp e combinar o melhor horário.
            </p>
          </div>
        </div>
      </section>

      {/* Contato + Formulário */}
      <section className="bg-white">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-20 md:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14">
            {/* Informações */}
            <div>
              <SectionHeading eyebrow="Informações" title="Fale com a gente." className="mb-10" />
              <div className="space-y-5">
                {[
                  { icon: 'ri-map-pin-line', label: 'Endereço', value: 'Av. Agamenon Magalhães, 1519\nMaurício de Nassau, Caruaru - PE\nCEP 55014-000' },
                  { icon: 'ri-whatsapp-line', label: 'WhatsApp', value: '(81) 98203-0285' },
                  { icon: 'ri-mail-line', label: 'E-mail', value: 'ednilsonleocadio@gmail.com' },
                  { icon: 'ri-time-line', label: 'Horário', value: 'Seg–Sex: 8h às 18h\nSáb: 9h às 13h' },
                ].map((item) => (
                  <div key={item.label} className="flex items-start gap-4">
                    <span className="w-10 h-10 flex items-center justify-center rounded-full bg-background-50 text-primary-600 flex-shrink-0">
                      <i className={item.icon}></i>
                    </span>
                    <div>
                      <p className="text-[11px] text-foreground-400 font-medium uppercase tracking-wider mb-1">{item.label}</p>
                      <p className="text-foreground-700 text-[14px] whitespace-pre-line">{item.value}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-10 rounded-2xl overflow-hidden border border-background-200/50" style={{ height: '320px' }}>
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3924.3456789!2d-36.01!3d-8.2836!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x7061a4e7b7b7b7b7%3A0x1234567890abcdef!2sAv.%20Agamenon%20Magalh%C3%A3es%2C%201519%20-%20Maur%C3%ADcio%20de%20Nassau%2C%20Caruaru%20-%20PE%2C%2055014-000!5e0!3m2!1spt-BR!2sbr!4v1746000000001!5m2!1spt-BR!2sbr"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Home Office Premium – Av. Agamenon Magalhães, 1519, Caruaru PE"
                ></iframe>
              </div>
            </div>

            {/* Formulário */}
            <div>
              <SectionHeading eyebrow="Orçamento" title="Solicitar orçamento." className="mb-10" />
              {submitted ? (
                <div className="bg-background-50 rounded-2xl p-10 text-center border border-background-200/50">
                  <div className="w-14 h-14 flex items-center justify-center bg-primary-600 rounded-full mx-auto mb-4">
                    <i className="ri-check-line text-white text-2xl"></i>
                  </div>
                  <h3 className="text-foreground-900 font-semibold text-xl mb-2">Mensagem enviada!</h3>
                  <p className="text-foreground-500 text-[14px] font-light">Entraremos em contato em até 24 horas úteis.</p>
                </div>
              ) : (
                <form data-readdy-form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-[11px] font-medium text-foreground-500 mb-2 uppercase tracking-wider">Nome *</label>
                      <input name="nome" required type="text" placeholder="Seu nome completo" className="w-full border border-background-200 rounded-lg px-4 py-3 text-[14px] text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 transition-colors" />
                    </div>
                    <div>
                      <label className="block text-[11px] font-medium text-foreground-500 mb-2 uppercase tracking-wider">Empresa</label>
                      <input name="empresa" type="text" placeholder="Nome da empresa" className="w-full border border-background-200 rounded-lg px-4 py-3 text-[14px] text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 transition-colors" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-[11px] font-medium text-foreground-500 mb-2 uppercase tracking-wider">E-mail *</label>
                    <input name="email" required type="email" placeholder="seu@email.com" className="w-full border border-background-200 rounded-lg px-4 py-3 text-[14px] text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 transition-colors" />
                  </div>
                  <div>
                    <label className="block text-[11px] font-medium text-foreground-500 mb-2 uppercase tracking-wider">Interesse</label>
                    <select name="interesse" className="w-full border border-background-200 rounded-lg px-4 py-3 text-[14px] text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors cursor-pointer bg-white">
                      <option value="">Selecione uma categoria</option>
                      <option value="agendar-showroom">Agendar Visita ao Showroom</option>
                      <option value="cadeiras-frisokar">Cadeiras - Frisokar</option>
                      <option value="cadeiras-plaxmetal">Cadeiras - Plaxmetal</option>
                      <option value="mesas-marzo-vitorino">Mesas - Marzo Vitorino</option>
                      <option value="kit-completo">Kit Completo (Cadeiras + Mesas)</option>
                      <option value="cabines-acusticas">Cabines Acústicas</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-[11px] font-medium text-foreground-500 mb-2 uppercase tracking-wider">Mensagem *</label>
                    <textarea name="mensagem" required rows={5} maxLength={500} placeholder="Descreva sua necessidade, quantidade de itens, prazo desejado..." className="w-full border border-background-200 rounded-lg px-4 py-3 text-[14px] text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 transition-colors resize-none"></textarea>
                    <p className="text-[11px] text-foreground-300 mt-1">Máximo 500 caracteres</p>
                  </div>
                  <button type="submit" disabled={loading} className="w-full bg-primary-600 text-white font-medium py-4 rounded-full hover:bg-primary-700 transition-colors duration-300 whitespace-nowrap cursor-pointer disabled:opacity-60">
                    {loading ? 'Enviando...' : 'Enviar Solicitação'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Versículo */}
      <section className="bg-foreground-950 py-20 md:py-24">
        <div className="max-w-3xl mx-auto px-5 text-center">
          <blockquote className="text-[22px] md:text-[28px] font-light text-white/90 leading-relaxed italic">
            "Entregue os seus projetos ao Senhor, e os seus planos serão bem sucedidos."
          </blockquote>
          <div className="mt-6 flex items-center justify-center gap-3">
            <span className="h-px w-8 bg-white/15"></span>
            <span className="text-[11px] font-semibold tracking-[0.2em] uppercase text-primary-500">Provérbios 16:3</span>
            <span className="h-px w-8 bg-white/15"></span>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppFloat />
    </div>
  );
}